import * as d3 from 'd3';
import { TiChartSeriesComponent } from "../components/ti-chart-series.component";
import { TILogoComponent } from "../components/ti-logo.component";
import { TooltipComponent } from "../components/tooltip.component";
import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { IndicatorHelpers } from "../helpers/indicator-helper";
import { StringHelpers } from "../helpers/string-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { TraceLoggingHelpers } from '../helpers/trace-logging-helpers';
import { TIDataPointToHighChartDataPointMapper } from "../mappers/data-point/ti-chart-point-to-highchart-data-point.mapper";
import { TIDataPointToHighChartVolumeDataPointMapper } from "../mappers/data-point/ti-chart-point-to-highchart-volume-data-point.mapper";
import { TIIndicatorPointToHighChartIndicatorPointMapper } from "../mappers/data-point/ti-indicator-point-to-highchart-indicator-point.mapper";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { TIIndicatorDataModel } from "../models/data-point/ti-indicator-data.model";
import { IndicatorOptionModel } from "../models/drop-down-list/indicator-option.model";
import { ChartType } from "../models/enum/chart-type.enum";
import { DrawingToolsType } from "../models/enum/drawing-tools-type.enum";
import { IndicatorType } from "../models/enum/indicator-type.enum";
import { Timeframe } from "../models/enum/timeframe.enum";
import { TIIndicatorDataToBBandsSeriesBuilder } from "./bbands-indicator-series.builder";
import { TIIndicatorDataToDCSeriesBuilder } from "./dc-indicator-series.builder";
import { TIIndicatorDataToEMACloudSeriesBuilder } from './ema-cloud-series.builder';
import { TIIndicatorDataToKeltnerChannelSeriesBuilder } from "./keltner-channel-indicator-series.builder";
import { TIIndicatorDataToMacdSeriesBuilder } from "./macd-indicator-series.builder";
import { ChartYAxisStateModel } from '../models/chart-style/chart-y-axis-state-model';
declare const window: any;
interface ChartSpacing {
    top: number;
    left: number;
    right: number;
    bottom: number;
}

export class HighChartBuilder {
    public static readonly mainChartAreaName = 'MainChartArea';
    public mainSeriesClassName = 'main-chart-series';

    private tiChartSeries: TiChartSeriesComponent;

    private indicatorsToHideFromMainYAxis = [IndicatorType.AVWAP, IndicatorType.DistanceFromEMA, IndicatorType.MovingAverageEMA,
    IndicatorType.MovingAverageSMA, IndicatorType.BBands, IndicatorType.EMACloud];

    private isDrawingToolRibbonActive = false;
    private isDrawingToolRibbonUpdate = false;
    private isDrawingToolStart = false;

    private xAxisTop = 0;
    public initIndicatorHeight: any = {};
    private chartSpacing: ChartSpacing = {
        top: 46,
        left: 0,
        right: 0,
        bottom: 0
    };

    constructor(tiChartSeries: TiChartSeriesComponent) {
        this.tiChartSeries = tiChartSeries;
    }

    public getConfig(): any {
        this.setChartSpacingTop();

        const config = this.buildConfig();
        return config;
    }

    public getChartSpacing(): ChartSpacing {
        return this.chartSpacing;
    }

    public drawingToolStartInActive() {
        setTimeout(() => {
            this.isDrawingToolStart = false;
        }, 150);
    }

    public drawingToolRibbonInActive() {
        setTimeout(() => {
            this.isDrawingToolRibbonActive = false;
        }, 150);
    }

    public drawingToolRibbonUpdate(executeCallback?: Function) {
        this.isDrawingToolRibbonUpdate = true;

        if (executeCallback) {
            executeCallback();
        }

        setTimeout(() => {
            this.isDrawingToolRibbonUpdate = false;
        }, 150);
    }

    public lineBetweenPoints = d3.line()
        .x((coordinates) => coordinates[0])
        .y((coordinates) => coordinates[1])
        .curve(d3.curveNatural);;

    private displayXAxisDateTimeTooltip(params: any) {
        const context = params as any;
        const wrapper = this.tiChartSeries.element.findFirst('.x-axis-label-wrapper');
        if (!wrapper || this.tiChartSeries.base.isPreview) {
            return;
        }

        const point = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == context.x);
        if (!point) {
            return;
        }

        const el = this.tiChartSeries.element.findFirst('.x-axis-label');
        const pElement = el?.querySelector('p');
        if (!el || !pElement) {
            return;
        }

        if (this.tiChartSeries?.chartStyleSettings?.primitiveMode) {
            return;
        }
 
        pElement.innerHTML = DateHelpers.format(TimeframeHelper.getDateTimeTooltipFormat(this.tiChartSeries.currentTimeframe), point.start);

        const xAxis = params.series.xAxis;
        const myWidth = el.clientWidth;
        const extraTop = this.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        const xAxisTop = xAxis.height + extraTop;

        let top = this.chartSpacing.top + xAxisTop;
        if (top < 2) {
            top = 2;
        }

        let left = context.plotX - myWidth / 2;
        if (left < 2) {
            left = 2;
        }
        
        wrapper.style.top = top + 'px';
        wrapper.style.left = left + 'px';

        wrapper.classList.add('active');
    }

    private hideXAxisDateTimeTooltip() {
        const wrapper = this.tiChartSeries.element.findFirst('.x-axis-label-wrapper');
        if (!wrapper) {
            return;
        }

        wrapper.classList.remove('active');
    }

    private setXAxisHeight() {
        const yAxis = this.tiChartSeries.highStockChart.yAxis[0] as any;
        const xAxis = this.tiChartSeries.highStockChart.xAxis[0] as any;
        if (!yAxis || !xAxis) {
            return;
        }

        if (yAxis.height != xAxis.height) {
            this.tiChartSeries.highStockChart.xAxis[0].update({
                height: yAxis.height
            });
        }
    }

    private setYAxisHeight() {
        const mainYAxisId = HighChartBuilder.mainChartAreaName + '-y-axis';
        const yAxis: any = this.tiChartSeries.highStockChart.get(mainYAxisId);
        if (!yAxis) {
            return;
        }

        const indicatorMainYAxisId = HighChartBuilder.mainChartAreaName + '-ind-y-axis';
        const indicatorMainYAxis: any = this.tiChartSeries.highStockChart.get(indicatorMainYAxisId);
        if (indicatorMainYAxis) {
            if (yAxis.height != indicatorMainYAxis.height) {
                const logoComponent = this.tiChartSeries.base.getComponentsByType(TILogoComponent)[0] as TILogoComponent;
                if (logoComponent) {
                    logoComponent.setPanelStyle();
                }

                indicatorMainYAxis.update({
                    height: yAxis.height
                });
            }

            if (yAxis.top != indicatorMainYAxis.top) {
                indicatorMainYAxis.update({
                    top: yAxis.top
                });
            }
        }

        const allIndicatorOptions = this.tiChartSeries.getAllActiveIndicatorOptions();
        const emaCloudIndicators = allIndicatorOptions.filter((x: IndicatorOptionModel) => { return x.value == IndicatorType.EMACloud });
        for (const ind of emaCloudIndicators) {
            const indYAxisId = ind.name + '-ind-y-axis';
            const indYAxis: any = this.tiChartSeries.highStockChart.get(indYAxisId);
            if (indYAxis) {
                if (yAxis.height != indYAxis.height) {
                    indYAxis.update({
                        height: yAxis.height
                    });
                }

                if (yAxis.top != indYAxis.top) {
                    indYAxis.update({
                        top: yAxis.top
                    });
                }
            }
        }
    }

    public getXAxisTop(): number {
        return this.xAxisTop;
    }

    private setAxisExtraLine() {
        const chartStyle = this.tiChartSeries.chartStyleSettings;
        const xAxis = this.tiChartSeries.highStockChart.xAxis[0] as any;
        const highchartContainer = this.tiChartSeries.element.findFirst('.highcharts-container');
        const existXAxisSeperatorElement = highchartContainer?.querySelectorAll('.axis-sep.x');
        const existYAxisSeperatorElement = highchartContainer?.querySelectorAll('.axis-sep.y');
        if (!chartStyle || !xAxis || this.tiChartSeries.base.isPreview) {
            return;
        }

        existXAxisSeperatorElement?.forEach((el: Element) => {
            el.remove();
        });
        existYAxisSeperatorElement?.forEach((el: Element) => {
            el.remove();
        });

        this.setChartSpacingTop();

        let chartExtraTop = this.chartSpacing.top;
        let yAxisSeperatorElementTop = 0;

        let rightSep = '70px';

        const chartExtraSmall = this.tiChartSeries.base.isExtraSmall();
        if (chartExtraSmall) {
            chartExtraTop = 20;
            yAxisSeperatorElementTop = 20;

            if (this.tiChartSeries.base.marketingWebChartMode) {
                // chartExtraTop = 24;
                // yAxisSeperatorElementTop = 24;
                chartExtraTop = 4;
                yAxisSeperatorElementTop = 4;
                rightSep = '56px';
            }
        }

        const extraTop = this.tiChartSeries.hasAnyComparisonSymbols() ? 20 : 0;
        this.xAxisTop = chartExtraTop + xAxis.height + extraTop;

        const xAxisSeperatorElement = document.createElement('div');
        const yAxisSeperatorElement = document.createElement('div');

        xAxisSeperatorElement.className = 'axis-sep x';
        xAxisSeperatorElement.style.top = this.xAxisTop + 'px';

        yAxisSeperatorElement.className = 'axis-sep y';

        if (chartStyle.showSmartRiskLevel && !chartExtraSmall) {
            xAxisSeperatorElement.style.right = '100px';
            yAxisSeperatorElement.style.right = '100px';
        } else {
            xAxisSeperatorElement.style.right = rightSep;
            yAxisSeperatorElement.style.right = rightSep;
        }

        if (this.tiChartSeries.base.pnpMode) {
            if (this.tiChartSeries.base.marketingWebChartMode) {
                xAxisSeperatorElement.style.right = '41px';
                yAxisSeperatorElement.style.right = '41px';
            } else {
                xAxisSeperatorElement.style.right = '55px';
                yAxisSeperatorElement.style.right = '55px';
            }

            yAxisSeperatorElementTop = chartExtraTop + 5;

            if (chartStyle.pnpChangePercentEnabled) {
                if (this.tiChartSeries.base.marketingWebChartMode) {
                    xAxisSeperatorElement.style.width = 'calc(100% - 42px)';
                    xAxisSeperatorElement.style.left = '0';
                    xAxisSeperatorElement.style.right = 'unset';
                }
                else {
                    xAxisSeperatorElement.style.width = 'calc(100% - 56px)';
                    xAxisSeperatorElement.style.left = '0';
                    xAxisSeperatorElement.style.right = 'unset';
                }
            }
        }

        if (yAxisSeperatorElementTop > 0) {
            yAxisSeperatorElement.style.top = `${yAxisSeperatorElementTop}px`;
            yAxisSeperatorElement.style.height = `calc(100% - ${yAxisSeperatorElementTop}px)`;
        }


        if (chartStyle.primitiveMode) {
            yAxisSeperatorElement.style.top = `0px`;
            xAxisSeperatorElement.style.display = 'none';
            yAxisSeperatorElement.style.right = '58px';
        }

        highchartContainer?.insertAdjacentElement('beforeend', xAxisSeperatorElement);
        highchartContainer?.insertAdjacentElement('beforeend', yAxisSeperatorElement);
    }

    private setChartSpacingTop() {
        const chartExtraSmall = this.tiChartSeries.base.isExtraSmall();
        if (this.tiChartSeries.base.marketingWebChartMode) {
            this.chartSpacing.top = 36;

            if (this.tiChartSeries.base.isPreview) {
                this.chartSpacing.top = 0;

                const statusElement = this.tiChartSeries.element.findFirst('.marketing-status-line');
                if (statusElement.classList.contains('top')) {
                    if (this.tiChartSeries.element.clientHeight <= 60) {
                        this.chartSpacing.top = statusElement.clientHeight + 6;
                    }
                    else {
                        this.chartSpacing.top = statusElement.clientHeight + 12;
                    }
                }
            } else if (chartExtraSmall) {
                // this.chartSpacing.top = 24;
                this.chartSpacing.top = 4;
            } else {
                if (this.tiChartSeries.base.pnpMode) {
                    this.mainSeriesClassName = 'main-chart-pnp-series';

                    if (this.tiChartSeries.element.clientHeight <= 230) {
                        this.chartSpacing.top = 24;
                    }
                } else {
                    if (this.tiChartSeries.base.element.containClass('one')) {
                        this.chartSpacing.top = 36;
                    } else {
                        this.chartSpacing.top = 58;
                    }
                }
            }
        } else {
            if (this.tiChartSeries.base.pnpMode) {
                this.chartSpacing.top = 0;
                if (this.tiChartSeries.chartStyleSettings?.pnpChangePercentEnabled) {
                    this.chartSpacing.top = 24;
                }
            } else {
                if (chartExtraSmall) {
                    this.chartSpacing.top = 20;
                } else {
                    if (this.tiChartSeries.base.element.containClass('one')) {
                        this.chartSpacing.top = 28;
                    } else {
                        this.chartSpacing.top = 46;
                    }
                }
            }
        }
    }

    private highchartInternalContainer: any;
    private buildConfig(): any {
        const me = this;
        const chartStlye = me.tiChartSeries.chartStyleSettings;
        if (!chartStlye) {
            return {};
        }

        const drawingToolManager = me.tiChartSeries.base.drawingToolsManager;
        const allIndicatorOptions = me.tiChartSeries.getAllActiveIndicatorOptions();
        const hasAnyNonMainChartAreaIndicators = allIndicatorOptions.some((x: IndicatorOptionModel) => x.chartAreaName != HighChartBuilder.mainChartAreaName);

        let clickNumber = 0;
        let cuvedPointsForNewAnnotation = new Array();

        this.chartSpacing.right = 94;

        const chartExtraSmall = this.tiChartSeries.base.isExtraSmall();
        if (chartStlye.showSmartRiskLevel && !chartExtraSmall) {
            this.chartSpacing.right = 124;
        }

        let height: number | null = null;
        this.chartSpacing.bottom = hasAnyNonMainChartAreaIndicators ? -22 : 0;
        this.chartSpacing.left = -60;
        if (me.tiChartSeries.base.isPreview) {
            height = this.tiChartSeries.element.clientHeight;
            this.chartSpacing.right = 0;
            this.chartSpacing.bottom = 0;
            this.chartSpacing.left = 0;
        } else if (me.tiChartSeries.base.marketingWebChartMode) {
            this.chartSpacing.bottom = hasAnyNonMainChartAreaIndicators ? -18 : 0;
            this.chartSpacing.right = 72;

            if (chartExtraSmall) {
                this.chartSpacing.left = 0;
                this.chartSpacing.right = 58;
            }
        }

        if (me.tiChartSeries.base.pnpMode) {
            this.chartSpacing.left = 0;
            this.chartSpacing.right = 56;
            this.setChartSpacingTop();

            if (chartStlye.pnpChangePercentEnabled) {
                this.chartSpacing.left = 6;
                if (!me.tiChartSeries.base.marketingWebChartMode) {
                    this.chartSpacing.right = 68;
                } else {
                    this.chartSpacing.right = 47;
                }
            } else {
                if (!me.tiChartSeries.base.marketingWebChartMode) {
                    this.chartSpacing.right = 68;
                } else {
                    this.chartSpacing.right = 41;
                }
            }
        }

        if (chartStlye.primitiveMode) {
            this.chartSpacing.left = 0;
            this.chartSpacing.right = 58;
        }
        
        const chartConfig: any = {
            boost: {
                useGPUTranslations: true, // Leverages GPU for faster rendering
                seriesThreshold: 1 // Boost mode kicks in even with 1 series
            },
            // series: {
            //     boostThreshold: 1000 // Adjust to kick in for large data sets
            // },
            plotOptions: {
                series: {
                    turboThreshold: 1000000,
                    animation: false,
                    point: {
                        events: {
                            remove: function () {
                                const point = this;
                                if (point && point.series && point.series.userOptions && point.series.userOptions.type == 'flags') {

                                    setTimeout(() => {
                                        drawingToolManager?.delete();
                                    }, 300);
                                }
                            },
                            update: function () {
                                const point = this;
                                if (point && point.series && point.series.userOptions && point.series.userOptions.type == 'flags') {

                                    setTimeout(() => {
                                        drawingToolManager?.saveAnnotations();
                                    }, 300);
                                }
                            }
                        }
                    }
                }
            },
            accessibility: {
                keyboardNavigation: {
                    enabled: false
                }
            },
            chart: {
                animation: false,
                spacingTop: this.chartSpacing.top,
                spacingRight: this.chartSpacing.right,
                spacingBottom: this.chartSpacing.bottom,
                spacingLeft: this.chartSpacing.left,
                styledMode: true,
                events: {
                    load: function () {
                        const highChartRoot = me.tiChartSeries.element.querySelector('.highcharts-root defs');
                        if (highChartRoot) {
                            const bgDefTags = me.tiChartSeries.highChartStyleBuilder.getTIChartBackgroundSVGDefs(chartStlye);
                            if (bgDefTags) {
                                highChartRoot.appendChild(bgDefTags);
                            }

                            const bgAreaDefTags = me.tiChartSeries.highChartStyleBuilder.getTIChartAreaBackgroundSVGDefs(chartStlye);

                            if (bgAreaDefTags) {
                                highChartRoot.appendChild(bgAreaDefTags);
                            }
                        }

                        const d3ParentScope = d3.select(me.tiChartSeries.element);
                        const onMouseMove = (e: any) => {
                            if (clickNumber == 2) {
                                if (cuvedPointsForNewAnnotation[0][1] >= e.chartY + 5) {
                                    cuvedPointsForNewAnnotation[1] = [(cuvedPointsForNewAnnotation[0][0] + e.chartX) / 2, e.chartY - 50];
                                }
                                else {
                                    cuvedPointsForNewAnnotation[1] = [(cuvedPointsForNewAnnotation[0][0] + e.chartX) / 2, e.chartY + 50];
                                }
                                cuvedPointsForNewAnnotation[2] = [e.chartX, e.chartY];

                                if (d3ParentScope.select('#dtcl_path_Guide').empty()) {
                                    const setting = me.tiChartSeries.base.drawingToolsManager?.drawingToolsSetting?.get('curvedLine');
                                    let stroke = 'rgb(41, 98, 255)';
                                    let width = '1';

                                    if (setting?.typeOptions?.line?.stroke) {
                                        stroke = setting?.typeOptions?.line?.stroke;
                                    }

                                    if (setting?.typeOptions?.line?.strokeWidth) {
                                        width = setting?.typeOptions?.line?.strokeWidth;
                                    }

                                    d3ParentScope.select(".highcharts-root").append("path").datum(cuvedPointsForNewAnnotation)
                                        .attr("d", me.lineBetweenPoints)
                                        .attr("id", "dtcl_path_Guide")
                                        .attr("class", "dtcl_GuideLine2")
                                        .attr("fill", "none")
                                        .attr("stroke", stroke)
                                        .attr("stroke-width", width);

                                }
                                else {
                                    d3ParentScope.select('#dtcl_path_Guide').attr('d', me.lineBetweenPoints(cuvedPointsForNewAnnotation));
                                }
                            }
                        };

                        if (me.highchartInternalContainer) {
                            me.highchartInternalContainer.removeEventListener('mousemove', onMouseMove);
                        }

                        me.highchartInternalContainer = this.container;
                        me.highchartInternalContainer.addEventListener('mousemove', onMouseMove);

                        me.tiChartSeries.element.findFirst('.main-chart')?.addClass('active');
                        me.tiChartSeries.onLoaded();

                        setTimeout(() => {
                            drawingToolManager?.sync(0);
                        }, 500);
                    },
                    click: (e) => {
                        const el = me.tiChartSeries.element.findFirst('.tooltip');
                        el?.removeClass('active');

                        const d3ParentScope = d3.select(me.tiChartSeries.element);
                        switch (clickNumber) {
                            case 1:
                                cuvedPointsForNewAnnotation[0] = [e.chartX, e.chartY];
                                ++clickNumber;
                                break;

                            case 2:
                                cuvedPointsForNewAnnotation[2] = [e.chartX, e.chartY];
                                drawingToolManager?.drawCustomAnnotation(DrawingToolsType.CurvedLine, cuvedPointsForNewAnnotation);
                                cuvedPointsForNewAnnotation = Array();
                                d3ParentScope.select('#dtcl_path_Guide').remove();
                                clickNumber = 0;
                                break;
                            default:
                                break;
                        }

                    },
                    redraw: (e) => {
                        TraceLoggingHelpers.log('Redraw', e)
                        me.setXAxisHeight();
                        me.setYAxisHeight();
                        me.setAxisExtraLine();
                        this.tiChartSeries.onRedraw();
                    },
                    afterAddSeries: function (e) {
                        if (e.series && e.series.userOptions && e.series.userOptions.type == 'flags' && !e.series.options.id) {
                            e.series.options.id = StringHelpers.newGuid();
                            drawingToolManager?.drawAnnotation(e.series);
                        }
                    },
                }
            },
            title: {
                text: '',
            },
            xAxis: this.getXAxis(allIndicatorOptions),
            yAxis: this.getYAxis(allIndicatorOptions),
            series: this.getSeries(allIndicatorOptions),
            colors: ['#ff0000'],
            scrollbar: {
                enabled: false
            },
            rangeSelector: {
                enabled: false
            },
            credits: {
                enabled: false
            },
            navigator: {
                enabled: false
            },
            exporting: {
                enabled: false
            },
            tooltip: {
                formatter: (p: any) => {
                    return '';
                },
                style: {
                    position: 'absolute',
                    top: '0'
                },
                positioner: (labelWidth, labelHeight, point) => {
                    let plot = {
                        x: point.plotX + labelWidth / 2 + 20,
                        y: point.plotY + labelHeight / 2 - 50
                    };

                    if (plot.x + labelWidth > window.innerWidth) {
                        plot.x = plot.x - (labelWidth + 40);
                    }

                    return plot;
                }
            },
            stockTools: {
                gui: {
                    buttons: []
                },
            },
            navigation: {
                annotationsOptions: {
                    events: {
                        add: (highChartDrawingToolsEvent: any) => {
                            if (highChartDrawingToolsEvent.target?.options) {
                                highChartDrawingToolsEvent.target.options.id = StringHelpers.newGuid();
                            }

                            drawingToolManager?.drawAnnotation(highChartDrawingToolsEvent.target);
                            me.tiChartSeries.onAnnotationAdd(highChartDrawingToolsEvent, false);
                        },
                        afterUpdate: (highChartDrawingToolsEvent: any) => {
                            if (highChartDrawingToolsEvent.target.options && !highChartDrawingToolsEvent.target.options.id) {
                                highChartDrawingToolsEvent.target.options.id = StringHelpers.newGuid();
                            }

                            me.tiChartSeries.base.drawingToolsManager?.updatePreCreatedAnnotationStyles(highChartDrawingToolsEvent.target, false);

                            me.drawingToolRibbonUpdate(() => {
                                const afterDrawingToolCompleted = highChartDrawingToolsEvent && highChartDrawingToolsEvent.target && !highChartDrawingToolsEvent.target.isUpdating;
                                if (highChartDrawingToolsEvent.target?.options?.typeOptions?.points?.length) {
                                    for (const p of highChartDrawingToolsEvent.target.options.typeOptions.points) {
                                        p.actualStartTimeT = null;
                                        p.actualXValue = null;
                                    }
                                }

                                if (afterDrawingToolCompleted || this.tiChartSeries.base.marketingWebChartMode) {
                                    drawingToolManager?.saveAnnotations();
                                }

                                me.tiChartSeries.onAnnotationAfterUpdate(highChartDrawingToolsEvent);
                            });
                        },
                        remove: () => {
                            me.tiChartSeries.base.drawingToolsManager?.delete();
                        }
                    },
                    shapeOptions: {
                        fill: 'none',
                        stroke: 'rgb(41, 98, 255)',
                    }
                },
                events: {
                    showPopup: (event: any) => {
                        me.isDrawingToolRibbonActive = true;
                        me.tiChartSeries.onPopupChange(true, event);
                    },
                    closePopup: (event: any) => {
                        me.tiChartSeries.onPopupChange(false, event);
                        me.drawingToolRibbonInActive();

                        const afterDrawingToolCompleted = event && event.target && !event.target.isUpdating;
                        if (afterDrawingToolCompleted) {
                            drawingToolManager?.saveAnnotations();
                        }
                    },
                    selectButton: () => {
                        me.isDrawingToolStart = true;
                    },
                    deselectButton: () => {
                        setTimeout(() => {
                            me.isDrawingToolStart = false;
                        }, 150);
                    }
                }
            }
        };

        const buttons = [
            // 'emojiCustom2Annotation',
            'simpleShapes',
            'crookedLines',
            // 'verticalLabels',
            //'advanced',
            //'measure',
            // 'indicators',
            // 'flags'
        ];

        if (chartStlye.emojiDrawingToolsEnabled) {
            buttons.unshift('emojiCustomAnnotation');
        }

        if (!chartStlye.curvedLineDisabled) {
            buttons.unshift('curvedLineCustomAnnotation');
        }

        buttons.unshift('lines');
        chartConfig.stockTools.gui = {
            buttons: buttons,

            definitions: {
                advanced: {
                    items: ['parallelChannel', 'timeCycles']
                },
                simpleShapes: {
                    items: ['label']
                },
                curvedLineCustomAnnotation: {
                    title: 'curved line',
                    className: 'highcharts-curved-line-custom-annotation'
                },
                emojiCustomAnnotation: {
                    title: 'emoji',
                    className: 'highcharts-emoji-custom-annotation'
                },
                emojiCustom2Annotation: {
                    title: 'emoji',
                    className: 'highcharts-emoji2-custom-annotation'
                },
            }
        };

        chartConfig.navigation.bindings = {
            curvedLineCustomAnnotation: {
                title: 'curved line',
                className: 'highcharts-curved-line-custom-annotation',
                init: function (e: any) {
                    // flagCurvedLilePlace = true;
                    clickNumber = 1;

                }
            },
            emojiCustomAnnotation: {
                title: 'emoji',
                className: 'highcharts-emoji-custom-annotation',
                init: function (e: any) {
                },
                start: function (e: any) {
                    e.emojiType = 1;
                    me.tiChartSeries.base.drawingToolsManager.drawCustomAnnotation(DrawingToolsType.Emoji, e);
                }
            },
            emojiCustom2Annotation: {
                title: 'emoji',
                className: 'highcharts-emoji2-custom-annotation',
                init: function (e: any) {
                },
                start: function (e: any) {
                    e.emojiType = 2;
                    me.tiChartSeries.base.drawingToolsManager.drawCustomAnnotation(DrawingToolsType.Emoji, e);
                    me.tiChartSeries.base.drawingToolsManager.removeRootDrawMode(false);
                }
            },
            labelAnnotation: {
                start: function (e: any) {
                    me.tiChartSeries.base.drawingToolsManager.drawCustomAnnotation(DrawingToolsType.NewTextBox, e);
                }
            }
        };

        if (height > 0) {
            chartConfig.chart.height = height;
        }

        if (!chartStlye.mobileZoomEnabled) {
            chartConfig.pinchType = 'none';
        }

        return chartConfig;
    }

    private getXAxis(allIndicators: Array<IndicatorOptionModel>): Array<any> {
        const mainXAxis = this.getMainChartXAxis();
        const result: Array<any> = [];
        const indicatorAxisAreas: Array<any> = [];
        for (const indicator of allIndicators) {
            if (!indicatorAxisAreas.some((x: string) => x == indicator.chartAreaName)) {
                indicatorAxisAreas.push(indicator.chartAreaName);
            }
        }
        const allIndicatorCount = indicatorAxisAreas.filter((x: string) => x != HighChartBuilder.mainChartAreaName).length;
        for (const indicator of allIndicators) {
            const indicatorConfig: any = IndicatorHelpers.getIndicatorHighChartConfig(indicator.value);
            if (indicatorConfig?.xAxis) {
                result.push(indicatorConfig.xAxis as any);
            }
        }

        if (allIndicatorCount == 1) {
            mainXAxis.height = '75%';
        } else if (allIndicatorCount > 1) {
            mainXAxis.height = '50%';
        }

        result.unshift(mainXAxis)
        return result;
    }

    private getMainChartXAxis(): any {
        const me = this;
        const xAxisConfigOption: any = {
            ordinal: true,
            type: "linear",
            crosshair: {
                className: 'highchart-main-crosshair x-axis',
                dashStyle: 'Dash',
                snap: false
            },
            panningEnabled: false,
            labels: {
                formatter: (p: any) => {
                    const point = this.tiChartSeries.tiChartPoints.find((x: TIChartPointModel) => x.xValue == p.value);

                    if (!point) {
                        return '';
                    }

                    if (!this.tiChartSeries.base.marketingWebChartMode && this.tiChartSeries.highChartAxisBuilder.xValuesForDarkersGridlines.some((x: number) => x == point.xValue)) {
                        p.tick.gridLine?.element?.classList.add('darker');
                        p.tick.label?.element?.classList.add('bold');
                    }

                    const isIntraday = this.tiChartSeries.highChartAxisBuilder.renderIsIntraday;
                    const timeframe = this.tiChartSeries.highChartAxisBuilder.renderTimeframe;
                    if (this.tiChartSeries.highChartAxisBuilder.xValuesForDateTime.some((x: number) => x == point.xValue)) {
                        if (!isIntraday) {
                            if (timeframe == Timeframe.Week) {
                                if (me.tiChartSeries.currentZoom >= -3 && me.tiChartSeries.currentZoom <= 1) {
                                    return DateHelpers.format('m-d', point.start);
                                }
                            }
                            return DateHelpers.format('SM-yy', point.start);
                        }

                        let dateSeparator = DateHelpers.format('m-d', point.start);
                        return dateSeparator;
                    } else if (this.tiChartSeries.highChartAxisBuilder.xValuesForDateTimeWithYear.some((x: number) => x == point.xValue)) {
                        return DateHelpers.format('m-d-yy', point.start);
                    } else if (this.tiChartSeries.highChartAxisBuilder.xValuesForHours.some((x: number) => x == point.xValue)) {
                        return DateHelpers.format('HH:MM', point.start);
                    }

                    return '';
                }
            },
            events: {
                setExtremes: (ev: any) => {
                    this.tiChartSeries.onExtremeChange(ev);
                }
            }
        };

        if (this.tiChartSeries.base.isPreview) {
            xAxisConfigOption.visible = false;
        }

        return xAxisConfigOption;
    }

    private getYAxis(allIndicators: Array<IndicatorOptionModel>): Array<any> {
        const mainYAxis = this.getMainChartYAxis();
        const result: Array<any> = [];
        const indicatorAxisAreas: Array<any> = [];
        const chartStyle = this.tiChartSeries.chartStyleSettings;
        for (const indicator of allIndicators) {
            if (!indicatorAxisAreas.some((x: string) => x == indicator.chartAreaName)) {
                indicatorAxisAreas.push(indicator.chartAreaName);
            }
        }
        const allIndicatorCount = indicatorAxisAreas.filter((x: string) => x != HighChartBuilder.mainChartAreaName).length;

        let foundAllIndicatorConfigCharts = true;
        let indicatorCount = 0;
        const padding = 3;
        const addedIndicators: Array<any> = [];
        const addedIndicatorsIdForResizer: Array<any> = [];
        const useYAxisHeightState = !this.tiChartSeries.base.pnpMode && !this.tiChartSeries.base.marketingWebChartMode && chartStyle?.chartYAxisStateModel?.currentVersion == ChartYAxisStateModel.Version;

        for (const indicator of allIndicators) {
            const indicatorConfig: any = IndicatorHelpers.getIndicatorHighChartConfig(indicator.value);
            if ((indicator.chartAreaName == HighChartBuilder.mainChartAreaName && !this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value))) {
                continue;
            }

            const chartExtraSmall = this.tiChartSeries.base.isExtraSmall();
            if (indicatorConfig?.yAxis) {
                if (chartStyle?.showSmartRiskLevel && !chartExtraSmall) {
                    indicatorConfig.yAxis.labels.x = 112;
                } else {
                    indicatorConfig.yAxis.labels.x = 80;
                }

                if (this.tiChartSeries.base.marketingWebChartMode) {
                    indicatorConfig.yAxis.labels.x = 70;
                }

                if (this.tiChartSeries.base.pnpMode) {
                    indicatorConfig.yAxis.labels.x = 55;
                }

                if (indicator.chartAreaName != HighChartBuilder.mainChartAreaName) {
                    indicatorConfig.yAxis.resize = {
                        enabled: true
                    };

                    indicatorConfig.yAxis.title = {
                        align: 'high',
                        offset: 0,
                        margin: 0,
                        text: indicator.text,
                        useHTML: true,
                        reserveSpace: false,
                        style: {
                            left: 0,
                        }
                    };

                    const id = indicator.chartAreaName + '-y-axis';
                    if (addedIndicators.some((x: string) => x == id)) {
                        continue;
                    }

                    if (useYAxisHeightState && !chartStyle.chartYAxisStateModel?.states?.some(x => x.name == id)) {
                        TraceLoggingHelpers.log('foundAllIndicatoConfigCharts not found', id)
                        foundAllIndicatorConfigCharts = false;
                    }

                    indicatorConfig.yAxis.id = id;
                    indicatorConfig.yAxis.labels.style.color = this.tiChartSeries.chartStyleSettings?.selectedPriceTextColor.toHex() ?? '';

                    if (allIndicatorCount == 1) {
                        if (this.tiChartSeries.base.marketingWebChartMode) {
                            indicatorConfig.yAxis.height = '14%';
                            indicatorConfig.yAxis.top = '86%';

                            if (indicator.value == IndicatorType.HighChartVolume) {
                                // The plot overflows ~18px below the container (negative spacingBottom fills the gap).
                                const maxVol = this.tiChartSeries.tiChartPoints.reduce((m, p) => Math.max(m, p.volume ?? 0), 0);
                                if (maxVol > 0) {
                                    indicatorConfig.yAxis.startOnTick = false;
                                    indicatorConfig.yAxis.min = -(maxVol * 0.22);
                                }
                            }
                        } else {
                            indicatorConfig.yAxis.height = '22%';
                            indicatorConfig.yAxis.top = '78%';
                        }
                    } else if (allIndicatorCount > 1) {
                        const scalePercentage = 50 / allIndicatorCount;
                        indicatorConfig.yAxis.height = (scalePercentage - padding) + '%';
                        indicatorConfig.yAxis.top = (53 + (scalePercentage * indicatorCount)) + '%';
                    }

                    indicatorCount++;
                    addedIndicators.push(id);
                    addedIndicatorsIdForResizer.push(id);
                } else if (this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) {
                    indicatorConfig.yAxis.resize = {
                        enabled: false
                    };
                    indicatorConfig.yAxis.labels.enabled = false;
                    indicatorConfig.yAxis.linkedTo = 0;
                    let id = indicator.chartAreaName + '-ind-y-axis';
                    if (indicator.value == IndicatorType.EMACloud) {
                        id = indicator.name + '-ind-y-axis';
                    }

                    if (addedIndicators.some((x: string) => x == id)) {
                        continue;
                    }

                    if (useYAxisHeightState && !chartStyle.chartYAxisStateModel?.states?.some(x => x.name == id)) {
                        TraceLoggingHelpers.log('foundAllIndicatoConfigCharts not found', id)
                        foundAllIndicatorConfigCharts = false;
                    }

                    indicatorConfig.yAxis.id = id;
                    addedIndicators.push(id);
                    if (allIndicatorCount == 1) {
                        indicatorConfig.yAxis.height = '75%';
                    } else if (allIndicatorCount > 1) {
                        indicatorConfig.yAxis.height = '50%';
                    } else {
                        indicatorConfig.yAxis.height = '100%';
                    }
                } else {
                    const id = indicator.chartAreaName + '-y-axis';
                    if (addedIndicators.some((x: string) => x == id)) {
                        continue;
                    }

                    if (useYAxisHeightState && !chartStyle.chartYAxisStateModel?.states?.some(x => x.name == id)) {
                        TraceLoggingHelpers.log('foundAllIndicatoConfigCharts not found', id)
                        foundAllIndicatorConfigCharts = false;
                    }

                    indicatorConfig.yAxis.id = id;
                    addedIndicators.push(id);
                }

                result.push(indicatorConfig.yAxis as any);
            }
        }

        if (useYAxisHeightState && !chartStyle.chartYAxisStateModel?.states?.some(x => x.name == mainYAxis.id)) {
            foundAllIndicatorConfigCharts = false;
        }

        if (!this.tiChartSeries.base.pnpMode) {
            const hiddenYAxisCongfigs: any = {
                id: HighChartBuilder.mainChartAreaName + '-hidden-y-axis',
                resize: {
                    enabled: false
                },
                labels: {
                    enabled: false
                },
                height: '100%'
            };

            if (allIndicatorCount == 1) {
                hiddenYAxisCongfigs.height = '75%';
            } else if (allIndicatorCount > 1) {
                hiddenYAxisCongfigs.height = '50%';
            }

            result.push(hiddenYAxisCongfigs);
        }

        if (allIndicatorCount == 1) {
            if (this.tiChartSeries.base.marketingWebChartMode) {
                mainYAxis.height = '82%';
            } else {
                mainYAxis.height = '75%';
            }
        } else if (allIndicatorCount > 1) {
            mainYAxis.height = '50%';
        }

        if (addedIndicatorsIdForResizer?.length) {
            mainYAxis.resize.controlledAxis = {
                next: [],
                prev: []
            };

            for (let i = 0; i < addedIndicatorsIdForResizer.length; i++) {
                const id = addedIndicatorsIdForResizer[i];
                if (i == 0) {
                    mainYAxis.resize.controlledAxis.next.push(id);
                }

                const yAxis = result.find((x: any) => x.id == id);
                if (!yAxis) {
                    continue;
                }

                yAxis.resize.controlledAxis = {
                    next: [],
                    prev: []
                };

                if (i + 1 < addedIndicatorsIdForResizer.length) {
                    const nextId = addedIndicatorsIdForResizer[i + 1];
                    yAxis.resize.controlledAxis.next.push(nextId);
                }
            }
        }

        result.unshift(mainYAxis);

        const existingIndicatorHeight = Object.keys(this.initIndicatorHeight);
        if (result.length != existingIndicatorHeight.length) {
            this.initIndicatorHeight = {};
        } else {
            for (const id of existingIndicatorHeight) {
                if (!result.some(x => x.id === id)) {
                    this.initIndicatorHeight = {};
                }
            }   
        }

        let setChartStates = false;
        let persistingIndicatorHeightEnabled = false;
        if (persistingIndicatorHeightEnabled && useYAxisHeightState && foundAllIndicatorConfigCharts && !this.tiChartSeries.base.pnpMode) {
            let stateMatch = true;
            let newChartArea = false;
            const states = chartStyle.chartYAxisStateModel?.states ?? [];
            for (const s of states) {
                if (!result.some((x => x.id == s.name))) {
                    stateMatch = false;
                    break;
                }

                if (s.name.indexOf(HighChartBuilder.mainChartAreaName) == -1 && s.name.indexOf('EMACLOUD') == -1) {
                    newChartArea = true;
                }
            }

            if (newChartArea && stateMatch) {
                const chartYAxisConfigs = {};
                let chartAreaHeight = 0;
                for (const yAxisConfig of result) {
                    const persitConfig = states.find(x => x.name == yAxisConfig.id);
                    if (!persitConfig) {
                        continue;
                    }

                    const height = parseFloat(persitConfig.height.replace('%', ''));
                    let top = parseFloat(persitConfig.top.replace('%', ''));
                    const totalBtwTop = height + top;
                    if (totalBtwTop > 100) {
                        let newTop = top - (totalBtwTop - 100);
                        persitConfig.top = newTop + '%';
                    }

                    chartYAxisConfigs[yAxisConfig.id] = {    
                        height: persitConfig.height,
                        top: persitConfig.top
                    };

                    if (yAxisConfig.id == HighChartBuilder.mainChartAreaName + '-y-axis' || yAxisConfig.id.indexOf('chartArea') == 0) {
                        chartAreaHeight += height;
                    }
                }

                if (chartAreaHeight >= 90) {
                    for (const yAxisConfig of result) {
                        const persitConfig = states.find(x => x.name == yAxisConfig.id);
                        if (!persitConfig) {
                            continue;
                        }

                        if (!this.initIndicatorHeight[yAxisConfig.id]) {
                            yAxisConfig.height = persitConfig.height;
                            yAxisConfig.top = persitConfig.top;
    
                            this.initIndicatorHeight[yAxisConfig.id] = {
                                height: persitConfig.height,
                                top: persitConfig.top
                            };
                        } else {
                            yAxisConfig.height = this.initIndicatorHeight[yAxisConfig.id].height;
                            yAxisConfig.top = this.initIndicatorHeight[yAxisConfig.id].top;
                        }
                    }
                    
                    setChartStates = true;
                }
            }

            if (!setChartStates) {
                const stateModel = new ChartYAxisStateModel();
                this.tiChartSeries.chartStyleSettings.chartYAxisStateModel = stateModel;
                this.tiChartSeries.base.chartService.requestSaveYAxisHeightState(stateModel);
            }
        }

        return result;
    }

    private getMainChartYAxis(): any {
        const me = this;
        const chartStyle = this.tiChartSeries.chartStyleSettings;

        let labelX = 80;
        let resizerEnable = true;
        const chartExtraSmall = this.tiChartSeries.base.isExtraSmall();
        if (chartStyle?.showSmartRiskLevel && !chartExtraSmall) {
            labelX = 112;
        }

        if (this.tiChartSeries.base.marketingWebChartMode) {
            resizerEnable = false;
            labelX = 70;

            if (chartExtraSmall) {
                labelX = 56;
            }
        }

        if (this.tiChartSeries.base.pnpMode) {
            resizerEnable = false;

            if (this.tiChartSeries.base.marketingWebChartMode) {
                labelX = 45;
            } else {
                labelX = 65;
            }
        }

        const options: any = {
            id: HighChartBuilder.mainChartAreaName + '-y-axis',
            gridLineColor: 'rgb(220, 220, 220, 0.2)',
            gridLineDashStyle: 'Dash',
            gridZIndex: 2,
            tickColor: chartStyle?.selectedPriceTextColor.toHex() ?? '',
            crosshair: {
                className: 'highchart-main-crosshair y-axis',
                dashStyle: 'Dash',
                snap: false
            },
            labels: {
                y: 4,
                x: labelX,
                style: {
                    color: chartStyle?.selectedPriceTextColor.toHex() ?? ''
                },
                formatter: function () {
                    const val = FormatHelpers.numberToString(parseFloat(this.value + ''));
                    if (me.tiChartSeries.chartStyleSettings?.isComparisonSymbolPercentageMode) {
                        return val + '%';
                    }

                    return '$' + val;
                }
            },
            resize: {
                enabled: resizerEnable
            }
        };

        if (chartStyle?.primitiveMode) {
            options.labels.x = undefined;
            options.labels.useHTML = true;
            options.resize.enabled = false;
        }

        return options;
    }

    private getSeries(allIndicators: Array<IndicatorOptionModel>): Array<any> {
        const mainSeries = this.getMainChartSeries();
        let result = [mainSeries];
        if (!this.tiChartSeries.base.pnpMode && this.tiChartSeries.chartStyleSettings.supportsFutureScrolling && this.tiChartSeries.chartStyleSettings.futureScrollingBarCount) {
            this.tiChartSeries.fakeTiChartPoints = [];
            const id = HighChartBuilder.mainChartAreaName + '-hidden-series';
            const hiddenSeries: any = {
                id: id,
                className: `hidden-series`,
                name: HighChartBuilder.mainChartAreaName + '-hidden-series',
                yAxis: HighChartBuilder.mainChartAreaName + '-hidden-y-axis',
                data: [],
                marker: {
                    enabled: false,
                    states: {
                        hover: {
                            enabled: false
                        }
                    }
                },
                states: {
                    inactive: {
                        opacity: 1
                    }
                },
                enableMouseTracking: false,
                dataGrouping: {
                    enabled: false
                }
            };

            const lastPoint = this.tiChartSeries.tiChartPoints?.[this.tiChartSeries.tiChartPoints?.length - 1];
            if (lastPoint) {
                let pointToCreate = this.tiChartSeries.chartStyleSettings.futureScrollingBarCount;
                if (this.tiChartSeries.base.marketingWebChartMode) {
                    pointToCreate += 100;
                } else {
                    pointToCreate += 1000;
                }

                for (let i = 1; i <= pointToCreate; i++) {
                    let newXValue = lastPoint.xValue + i;
                    const point = TIDataPointToHighChartDataPointMapper.toHighChartDataPoint(this.tiChartSeries, lastPoint);
                    point.x = newXValue;
                    point.className = 'hidden-point';

                    hiddenSeries.data.push({
                        x: newXValue,
                        y: 0,
                        open: 0,
                        high: 0,
                        low: 0,
                        close: 0
                    });

                    this.tiChartSeries.lastHiddenPointXValue = newXValue;
                    this.tiChartSeries.fakeTiChartPoints.push(point);
                }

            }

            result.push(hiddenSeries);
        }

        // let result = [mainSeries];
        const mainYAxis = HighChartBuilder.mainChartAreaName + '-y-axis';
        for (const indicator of allIndicators) {
            const indicatorConfig = IndicatorHelpers.getIndicatorHighChartConfig(indicator.value);
            if (!indicatorConfig?.series) {
                continue;
            }

            if (indicator.highChartIndicator) {
                const series: any = indicatorConfig.series[0];
                if (indicator.chartAreaName != HighChartBuilder.mainChartAreaName) {
                    series.yAxis = indicator.chartAreaName + '-y-axis';
                    series.lastPrice = {
                        className: 'last-price-indicator-' + indicator.getClassName(),
                        enabled: true
                    };
                }

                if (indicator.value == IndicatorType.HighChartVolume) {
                    const builtDataPoints = TIDataPointToHighChartVolumeDataPointMapper.map(this.tiChartSeries.tiChartPoints);
                    series.data = builtDataPoints.points;
                }

                let className = indicator.getClassName();
                const rawIndicatorData = this.tiChartSeries.tiIndicatorsData
                    .find((x: TIIndicatorDataModel) => indicator.highChartIndicator && x.indicatorType == indicator.value && x.chartAreaName == indicator.chartAreaName);
                if (rawIndicatorData) {
                    className = rawIndicatorData.getClassName();
                }

                series.className = 'indicator-series ' + className;
                result.push(series);
            } else {
                const rawIndicatorData = this.tiChartSeries.tiIndicatorsData
                    .find((x: TIIndicatorDataModel) => x.name == indicator.name && x.chartAreaName == indicator.chartAreaName);
                if (rawIndicatorData) {
                    if (indicator.value == IndicatorType.MACD) {
                        if (indicator.chartAreaName == HighChartBuilder.mainChartAreaName) {
                            continue;
                        }

                        const macdSeries = TIIndicatorDataToMacdSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, mainYAxis);
                        result = result.concat(macdSeries);
                    } else if (indicator.value == IndicatorType.DonchianChanel) {
                        const dcSeries = TIIndicatorDataToDCSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, mainYAxis);
                        result = result.concat(dcSeries);
                    } else if (indicator.value == IndicatorType.BBands) {
                        let yAxis = '';
                        let useLastPrice = false;

                        if (this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) {
                            yAxis = rawIndicatorData.chartAreaName + '-ind-y-axis';
                        } else {
                            yAxis = rawIndicatorData.chartAreaName + '-y-axis';

                            if (rawIndicatorData.chartAreaName != HighChartBuilder.mainChartAreaName) {
                                useLastPrice = true;
                            }
                        }

                        const bbandSeries = TIIndicatorDataToBBandsSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, useLastPrice, yAxis);
                        result = result.concat(bbandSeries);
                    } else if (indicator.value == IndicatorType.KeltnerChannel) {
                        const keltnerSeries = TIIndicatorDataToKeltnerChannelSeriesBuilder.map(indicator, rawIndicatorData, indicatorConfig, mainYAxis);
                        result = result.concat(keltnerSeries);
                    } else if (indicator.value == IndicatorType.EMACloud) {
                        const emaCloudSeries = TIIndicatorDataToEMACloudSeriesBuilder.map(rawIndicatorData, indicatorConfig);
                        result = result.concat(emaCloudSeries);
                    } else {
                        const indicatorSeries: any = indicatorConfig.series[0];
                        const needZone = rawIndicatorData.needHighChartZone(indicatorSeries.type);
                        const builtDataPoints = TIIndicatorPointToHighChartIndicatorPointMapper.map(rawIndicatorData, indicator.value, needZone);
                        indicatorSeries.name = indicator.name;
                        indicatorSeries.data = builtDataPoints.points;
                        indicatorSeries.className = 'indicator-series ' + rawIndicatorData.getClassName();
                        if (needZone) {
                            rawIndicatorData.zones = builtDataPoints.zones;
                            indicatorSeries.zones = builtDataPoints.zones;
                            indicatorSeries.zoneAxis = 'x';
                        }
                        if (indicator.chartAreaName != HighChartBuilder.mainChartAreaName) {
                            indicatorSeries.yAxis = rawIndicatorData.chartAreaName + '-y-axis';
                            indicatorSeries.lastPrice = {
                                className: 'last-price-indicator-' + indicator.getClassName(),
                                enabled: true
                            };
                        } else {
                            if (this.indicatorsToHideFromMainYAxis.some((x: IndicatorType) => x == indicator.value)) {
                                indicatorSeries.yAxis = rawIndicatorData.chartAreaName + '-ind-y-axis';
                            } else {
                                indicatorSeries.yAxis = rawIndicatorData.chartAreaName + '-y-axis';
                            }

                        }
                        result.push(indicatorSeries);
                    }
                }
            }
        }

        return result;
    }

    private getMainChartSeries(): any {
        const result: any = {};
        const me = this;
        const chartStyle = this.tiChartSeries.chartStyleSettings;
        if (!chartStyle) {
            return result;
        }

        const chartType = chartStyle.chartType;

        const dataPoints = TIDataPointToHighChartDataPointMapper.map(me.tiChartSeries);
        result.id = HighChartBuilder.mainChartAreaName;
        result.data = dataPoints.points;
        result.className = this.mainSeriesClassName;
        if (chartType == ChartType.Area) {
            result.type = 'area';
            result.lineWidth = chartStyle.chartTypeLineWidth;
            result.threshold = null;

            if (!chartStyle.isOneColor) {
                result.zones = dataPoints.zones;
                result.zoneAxis = 'x';
            }
        } else if (chartType == ChartType.Line) {
            result.type = 'line';
            result.lineWidth = chartStyle.chartTypeLineWidth;
            if (!chartStyle.isOneColor) {
                result.zones = dataPoints.zones;
                result.zoneAxis = 'x';
            }
        } else if (chartType == ChartType.OHLC) {
            result.type = 'ohlc';
            result.groupPadding = 0;
            result.pointPadding = 0.1;
        } else if (chartType == ChartType.HLC) {
            result.type = 'hlc';
            result.groupPadding = 0;
            result.pointPadding = 0.1;
        } else if (chartType == ChartType.Bar) {
            result.type = 'column';
            result.groupPadding = 0.1;
            result.pointPadding = 0;
            result.threshold = null;
        } else {
            result.type = 'candlestick';
            result.groupPadding = 0.1;
            result.pointPadding = 0.1;
            result.lineWidth = 1;
        }

        if (chartType == ChartType.Line || chartType == ChartType.Area) {
            result.marker = {
                enabled: false,
                states: {
                    hover: {
                        enabled: false
                    }
                }
            };

            result.states = {
                inactive: {
                    opacity: 1
                }
            };
        }
        result.lastPrice = {
            className: 'main-series-last-price',
            enabled: true
        };

        result.zIndex = 99;
        result.boostThreshold = 1000;
        result.dataGrouping = {
            enabled: false
        };
        result.point = {
            events: {
                click: function () {
                    const chart: any = me.tiChartSeries.highStockChart;
                    if (!!chart.navigationBindings?.activeAnnotation ||
                        me.isDrawingToolStart ||
                        me.isDrawingToolRibbonActive ||
                        me.isDrawingToolRibbonUpdate ||
                        (me.tiChartSeries.base.isPreview && !window.trace)) {
                        return;
                    }

                    const params: any = this;
                    const toolTip = me.tiChartSeries.base.getComponentsByType(TooltipComponent)[0] as TooltipComponent;
                    toolTip?.renderTooltip(params.x);
                    toolTip?.show();
                },
                mouseOver: function () {
                    me.displayXAxisDateTimeTooltip(this);
                },
                mouseOut: function () {
                    me.hideXAxisDateTimeTooltip();
                }
            }
        };

        return result;
    }
}
